import { useState } from 'react';
import axiosService from 'utils/axios';
import { ErrorResponse } from 'types/general/types';
import { prepareForUpload } from 'utils/images/prepareForUpload';
import { resolveImageUploadSettings } from 'utils/images/settings';
import { FILE_TYPE_PICTURE } from '../../config';

interface UploadResult {
  successful: string[];
  failed: { file: string; error: string }[];
}

export interface UploadProgress {
  isUploading: boolean;
  currentFile: number;
  totalFiles: number;
  currentFileName: string;
  percentage: number;
  // Overrides the default "Uploading N of M files" overlay label, e.g. for a delete batch
  label?: string;
}

async function uploadFilesSequentially(
  vehicleId: string,
  fileType: string,
  files: File[],
  onProgress?: (progress: UploadProgress) => void,
  tabId?: string
): Promise<UploadResult> {
  const results: UploadResult = {
    successful: [],
    failed: [],
  };
  const imageSettings = resolveImageUploadSettings();

  for (let i = 0; i < files.length; i += 1) {
    try {
      // Update progress at the start of each file upload
      onProgress?.({
        isUploading: true,
        currentFile: i + 1,
        totalFiles: files.length,
        currentFileName: files[i].name,
        percentage: Math.round((i / files.length) * 100),
      });

      const formData = new FormData();
      if (fileType === FILE_TYPE_PICTURE) {
        // The field name stays the original name: the tab map and the results are keyed by it.
        formData.append(files[i].name, await prepareForUpload(files[i], imageSettings));
        if (tabId) {
          formData.append('tab_id', tabId);
        }
        await axiosService.post(`/vehicles/vehicle/${vehicleId}/add_pictures/`, formData, {
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const overallProgress = Math.round(
                ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
              );
              onProgress?.({
                isUploading: true,
                currentFile: i + 1,
                totalFiles: files.length,
                currentFileName: files[i].name,
                percentage: overallProgress,
              });
            }
          },
        });
        results.successful.push(files[i].name);
      } else {
        formData.append(fileType, files[i], files[i].name);
        await axiosService.post(`/vehicles/vehicle/${vehicleId}/add_files/`, formData, {
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const overallProgress = Math.round(
                ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
              );
              onProgress?.({
                isUploading: true,
                currentFile: i + 1,
                totalFiles: files.length,
                currentFileName: files[i].name,
                percentage: overallProgress,
              });
            }
          },
        });
        results.successful.push(files[i].name);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Upload failed';
      results.failed.push({
        file: files[i].name,
        error: errorMessage,
      });
    }
  }

  // Update progress to indicate completion
  onProgress?.({
    isUploading: false,
    currentFile: files.length,
    totalFiles: files.length,
    currentFileName: '',
    percentage: 100,
  });

  return results;
}

export const useFileUpload = () => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    currentFile: 0,
    totalFiles: 0,
    currentFileName: '',
    percentage: 0,
  });

  const uploadFiles = (
    vehicleId: string,
    fileType: string,
    files: File[],
    onSuccess?: () => void,
    onError?: (error: ErrorResponse) => void,
    trackProgress = false,
    tabId?: string
  ) => {
    const onProgress = trackProgress ? setUploadProgress : undefined;

    if (trackProgress) {
      setUploadProgress({
        isUploading: true,
        currentFile: 0,
        totalFiles: files.length,
        currentFileName: '',
        percentage: 0,
      });
    }

    return uploadFilesSequentially(vehicleId, fileType, files, onProgress, tabId)
      .then((results: UploadResult) => {
        // Handle results
        if (results.failed.length === 0) {
          // All files uploaded successfully
          onSuccess?.();
          return { success: true, results };
        } else if (results.successful.length === 0) {
          // All files failed
          const failedFilesList = results.failed.map(f => `${f.file}: ${f.error}`).join('\n');
          onError?.({
            status: 'error',
            statusText: 'Upload Failed',
            message: `All files failed to upload:\n${failedFilesList}`,
          });
          return { success: false, results };
        }
        // Partial success
        const failedFiles = results.failed.map(f => f.file).join(', ');
        onError?.({
          status: 'warning',
          statusText: 'Partial Upload Success',
          message: `${results.successful.length} of ${files.length} files uploaded successfully. Failed files: ${failedFiles}`,
        });
        return { success: false, results };
      })
      .catch((err: ErrorResponse) => {
        onError?.(err);
        return { success: false, results: { successful: [], failed: [] } };
      })
      .finally(() => {
        if (trackProgress) {
          setUploadProgress({
            isUploading: false,
            currentFile: 0,
            totalFiles: 0,
            currentFileName: '',
            percentage: 0,
          });
        }
      });
  };

  return {
    uploadFiles,
    uploadProgress,
  };
};
